// Revaxis® is a booster-only vaccine. It must not be used for primary
// immunisation. The indicationSummary makes this explicit. There is only
// one brand and one schedule — the runner will auto-select both without
// prompting the pharmacist.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.DTP_REVAXIS,
  displayName: "DTP booster (Revaxis\u00ae)",
  serviceType: "administration",
  minimumAgeYears: 6,
  indicationSummary:
    "Booster vaccination against diphtheria, tetanus, and poliomyelitis in patients who have completed a primary course. Revaxis\u00ae is NOT for primary immunisation. A booster is indicated where the last relevant antigen dose was more than 10 years ago, or more than 5 years ago where travel to a risk area justifies bringing reinforcing immunisation forward.",

  brands: [
    {
      brandId: "revaxis",
      brandName: "Revaxis\u00ae",
      minimumAgeYears: 6,
      route: "IM",
      doseDescription: "0.5 mL single dose",
      administrationNotes:
        "Shake the pre-filled syringe to obtain a homogeneous, turbid white suspension. Inspect before use. Administer by intramuscular injection into the deltoid muscle. Do not use the intravenous, subcutaneous, or intradermal route under normal circumstances. Subcutaneous administration is permitted only for patients with bleeding disorders where intramuscular injection is contraindicated \u2014 document this clinical decision. Do not use the gluteal region.",
    },
  ],

  schedules: [
    {
      scheduleId: "revaxis-booster",
      label: "Single booster dose",
      offLabel: false,
      minimumAgeYears: 6,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 single booster dose",
        },
      ],
      completionNotes:
        "Document the justification for the booster interval selected (standard 10-year interval, or brought forward due to travel risk). Revaxis\u00ae is a booster only \u2014 if the patient has not completed a primary diphtheria, tetanus, and polio course, this product is not appropriate under this PGD. Refer to the patient's GP for primary immunisation.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "revaxis-ex-under-6",
      question: "Is the patient under 6 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Revaxis\u00ae is not licensed for patients under 6 years of age. Do not proceed.",
      referralMessage:
        "Refer to the patient's GP for age-appropriate diphtheria, tetanus, and polio vaccination.",
    },
    {
      checkId: "revaxis-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to diphtheria, tetanus, or polio vaccines, or to any component or residual of Revaxis\u00ae (including formaldehyde, neomycin, streptomycin, or polymyxin B)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Revaxis\u00ae is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "revaxis-ex-pku",
      question: "Has the patient been diagnosed with phenylketonuria (PKU)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Revaxis\u00ae contains phenylalanine and is contraindicated in patients with phenylketonuria. Do not proceed.",
      referralMessage:
        "Refer to the patient's GP for advice on alternative immunisation.",
    },
    {
      checkId: "revaxis-ex-no-primary-course",
      question:
        "Has the patient NOT completed a primary course of diphtheria, tetanus, and polio vaccination?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Revaxis\u00ae is a booster vaccine only and is not appropriate where a primary course has not been completed. Do not supply under this PGD.",
      referralMessage:
        "Refer to the patient's GP for primary immunisation against diphtheria, tetanus, and polio.",
    },
    {
      checkId: "revaxis-ex-recent-booster",
      question:
        "Has the patient received a booster dose of a diphtheria or tetanus-toxoid-containing vaccine within the last 5 years?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A minimum interval of 5 years is required between diphtheria or tetanus-toxoid-containing booster doses. Vaccination must be deferred until the 5-year interval has elapsed.",
      referralMessage: "Rebook when the minimum interval has been reached.",
    },
    {
      checkId: "revaxis-ex-neurological-prior",
      question:
        "Has the patient had a neurological complication following a previous dose of a diphtheria or tetanus-toxoid-containing immunisation?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A history of neurological complication following a previous diphtheria or tetanus-toxoid immunisation requires specialist assessment before proceeding. Do not supply under this PGD.",
      referralMessage:
        "Refer to the patient's GP or a specialist immunisation service for individual risk assessment.",
    },
  ],

  cautions: [
    {
      checkId: "revaxis-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "Revaxis\u00ae may be administered by subcutaneous injection in patients where intramuscular injection is contraindicated due to a bleeding disorder. Apply firm pressure for at least two minutes after injection. For patients on stable warfarin with INR within therapeutic range, intramuscular injection may proceed using a fine-gauge needle. Document the clinical decision and the route used.",
    },
    {
      checkId: "revaxis-caut-pregnancy",
      question: "Is the patient pregnant?",
      cautionMessage:
        "Revaxis\u00ae should only be used in pregnancy where it is urgent to boost immunity against diphtheria, tetanus, or polio (e.g. a tetanus-prone wound or high-risk travel that cannot be deferred). Refer to the patient's GP before proceeding in pregnancy. Breastfeeding is not a contraindication.",
    },
    {
      checkId: "revaxis-caut-gbs-brachial",
      question:
        "Has the patient had a previous episode of Guillain-Barr\u00e9 syndrome or brachial neuritis following a tetanus-toxoid-containing vaccine?",
      cautionMessage:
        "A history of Guillain-Barr\u00e9 syndrome or brachial neuritis following tetanus-toxoid vaccination is a caution. Refer to the patient's GP before proceeding to assess individual risk and benefit.",
    },
    {
      checkId: "revaxis-caut-immunosuppressed",
      question: "Is the patient immunosuppressed due to disease or treatment?",
      cautionMessage:
        "Immunosuppression may reduce the immune response to Revaxis\u00ae. Vaccination should still proceed as the vaccine is inactivated and safe. Counsel that protection may be suboptimal.",
    },
    {
      checkId: "revaxis-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine boosts your protection against diphtheria, tetanus, and poliomyelitis. It is a booster dose only and is not a primary vaccination.",
    "Revaxis\u00ae may cause transient vertigo (dizziness) in some people. Be aware of this before driving or operating machinery after vaccination.",
    "Tetanus can enter the body through wounds, cuts, and abrasions. If you sustain a tetanus-prone wound (a deep, dirty, or puncture wound; a wound contaminated with soil, manure, or foreign bodies; or a wound with devitalised tissue) seek medical attention urgently, regardless of your vaccination status.",
    "If you develop any unusual neurological symptoms (weakness, tingling, or numbness in the limbs) or signs of a severe allergic reaction after leaving the pharmacy, seek medical attention immediately and inform the clinician that you have recently received a diphtheria, tetanus, and polio vaccination.",
    "Your next booster will typically be due in 10 years, unless a tetanus-prone wound or further travel risk makes an earlier booster necessary.",
  ],
});
